import React from "react";
import {
  ScrollView,
  StyleSheet,
  View,
  type LayoutChangeEvent,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
} from "react-native";

import {
  Text,
  Divider,
  useTheme,
  Portal,
  ActivityIndicator,
} from "react-native-paper";
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
  Easing,
} from "react-native-reanimated";

import { useGameStore } from "../stores/useGameStoreFacade";
import { useAssetStore } from "../stores/useAssetStore";
import { useModalStore } from "../stores/useModalStore";
import type { ExtendedTheme } from "../theme/SynapseTheme";
import type {
  MoveJudgement,
  BacktrackReportEntry,
} from "../utils/gameReportUtils";
import Modal from "./AppModal";
import AppButton from "./ui/AppButton";
import ModalHeader from "./ui/ModalHeader";
import BottomFade from "./ui/BottomFade";

interface WordDefinitionDialogProps {
  word: string;
  visible: boolean;
  onDismiss: () => void;
  pathIndexInPlayerPath?: number | null;
}

const WordDefinitionDialog: React.FC<WordDefinitionDialogProps> = ({
  word,
  visible,
  onDismiss,
  pathIndexInPlayerPath,
}) => {
  const theme = useTheme() as ExtendedTheme;
  const { colors, customColors } = theme;

  const definitionsData = useAssetStore((state) => state.definitionsData);
  const ensureDefinitionsLoaded = useAssetStore(
    (state) => state.ensureDefinitionsLoaded,
  );
  const playerPath = useGameStore((state) => state.playerPath);
  const optimalChoices = useGameStore((state) => state.optimalChoices);
  const gameStatus = useGameStore((state) => state.gameStatus);
  const backtrackToWord = useGameStore((state) => state.backtrackToWord);
  const backtrackHistory = useGameStore((state) => state.backtrackHistory);
  const startWord = useGameStore((state) => state.startWord);
  const targetWord = useGameStore((state) => state.targetWord);
  const currentWord = useGameStore((state) => state.currentWord);

  // Bottom fade while more definitions sit below the fold
  const [viewportH, setViewportH] = React.useState(0);
  const [contentH, setContentH] = React.useState(0);
  const [atBottom, setAtBottom] = React.useState(false);
  const showFade = contentH > viewportH + 4 && !atBottom;
  const onScroll = (e: NativeSyntheticEvent<NativeScrollEvent>) => {
    const { contentOffset, layoutMeasurement, contentSize } = e.nativeEvent;
    setAtBottom(
      contentOffset.y + layoutMeasurement.height >= contentSize.height - 8,
    );
  };

  // NEW: On-demand definitions loading state
  const [isLoadingDefinitions, setIsLoadingDefinitions] = React.useState(false);
  const [localDefinitions, setLocalDefinitions] = React.useState<
    string[] | null
  >(null);

  // Current game report, for the post-game Detour info
  const gameReportModalReport = useModalStore(
    (state) => state.gameReportModalReport,
  );

  const scale = useSharedValue(0.9);
  const opacity = useSharedValue(0);

  // NEW: Load definitions on-demand when dialog opens
  React.useEffect(() => {
    if (visible && word) {
      // Start animation
      scale.value = withTiming(1, {
        duration: 250,
        easing: Easing.out(Easing.back(1.5)),
      });
      opacity.value = withTiming(1, { duration: 250 });

      // Definitions load on first use (not at startup): read them from the
      // store, loading them once if this is the first dialog of the session.
      let cancelled = false;
      const showDefinitions = async () => {
        if (definitionsData) {
          setLocalDefinitions(definitionsData[word] || []);
          setIsLoadingDefinitions(false);
          return;
        }

        setIsLoadingDefinitions(true);
        try {
          const definitions = await ensureDefinitionsLoaded();
          if (!cancelled) setLocalDefinitions(definitions[word] || []);
        } catch (error) {
          console.error("Failed to load definitions:", error);
          if (!cancelled) setLocalDefinitions([]);
        } finally {
          if (!cancelled) setIsLoadingDefinitions(false);
        }
      };

      showDefinitions();
      return () => {
        cancelled = true;
      };
    } else {
      // Close animation
      scale.value = withTiming(0.9, {
        duration: 150,
        easing: Easing.in(Easing.ease),
      });
      opacity.value = withTiming(0, { duration: 150 });

      // Reset local state when closing
      if (!visible) {
        setLocalDefinitions(null);
        setIsLoadingDefinitions(false);
      }
    }
  }, [visible, word, scale, opacity, definitionsData, ensureDefinitionsLoaded]);

  const animatedContentStyle = useAnimatedStyle(() => {
    return {
      transform: [{ scale: scale.value }],
      opacity: opacity.value,
    };
  });

  const canBacktrackToWord = React.useMemo(() => {
    const wordIndex =
      typeof pathIndexInPlayerPath === "number"
        ? pathIndexInPlayerPath
        : playerPath.indexOf(word);
    if (gameStatus !== "playing" || !playerPath || playerPath.length <= 1)
      return false;
    if (wordIndex <= 0 || wordIndex >= playerPath.length - 1) return false;
    const choiceIndex = wordIndex - 1;
    if (choiceIndex < 0 || choiceIndex >= optimalChoices.length) return false;
    const choice = (optimalChoices as MoveJudgement[])[choiceIndex];
    return (
      (choice.isGlobalOptimal || choice.isLocalOptimal) &&
      !choice.usedAsCheckpoint &&
      !(backtrackHistory as BacktrackReportEntry[]).some(
        (event) => event.landedOn === word,
      )
    );
  }, [
    word,
    pathIndexInPlayerPath,
    playerPath,
    optimalChoices,
    gameStatus,
    backtrackHistory,
  ]);

  // Post-game: for a Detour, where the gold path went from the same word
  const detourInfo = React.useMemo(() => {
    // Only show for completed games
    if (gameStatus === "playing" || !gameReportModalReport) return null;

    const wordIndex =
      typeof pathIndexInPlayerPath === "number"
        ? pathIndexInPlayerPath
        : playerPath.indexOf(word);

    // Only show for words that were the result of a choice (not the start word)
    if (wordIndex <= 0) return null;

    const choiceIndex = wordIndex - 1;
    const reportOptimalChoices = gameReportModalReport.optimalChoices;

    if (choiceIndex < 0 || choiceIndex >= reportOptimalChoices.length)
      return null;

    const choice = reportOptimalChoices[choiceIndex];

    // Only for a Detour (neither Global Optimal nor Local Optimal) made from
    // a gold-path word; `optimalChoice` is the next gold-path word (Ruling R4).
    if (choice.isGlobalOptimal || choice.isLocalOptimal) return null;
    if (!choice.optimalChoice) return null;

    // Colour the gold-path word gold when it is on the gold path from the
    // player's word, else purple when it is on the Local Optimal route.
    const goldPath = gameReportModalReport.optimalPath;
    const localOptimalRoute = gameReportModalReport.suggestedPath;

    const posOnGold = goldPath.indexOf(choice.playerPosition);
    const posOnLocal = localOptimalRoute.indexOf(choice.playerPosition);

    const isGlobalOptimal =
      posOnGold >= 0 &&
      posOnGold < goldPath.length - 1 &&
      goldPath[posOnGold + 1] === choice.optimalChoice;

    const isLocalOptimal =
      posOnLocal >= 0 &&
      posOnLocal < localOptimalRoute.length - 1 &&
      localOptimalRoute[posOnLocal + 1] === choice.optimalChoice;

    return {
      playerChoice: choice.playerChose,
      goldNext: choice.optimalChoice,
      playerPosition: choice.playerPosition,
      isGlobalOptimal,
      isLocalOptimal,
    };
  }, [
    gameStatus,
    gameReportModalReport,
    word,
    pathIndexInPlayerPath,
    playerPath,
  ]);

  // The word's role on the board: drives the title colour, with a text label so
  // the role never depends on colour alone.
  const role = React.useMemo((): { label: string; color: string } | null => {
    if (word === targetWord)
      return { label: "Target word", color: customColors.endNode };
    if (word === startWord)
      return { label: "Start word", color: customColors.startNode };
    if (gameStatus === "playing" && word === currentWord)
      return { label: "Current word", color: customColors.currentNode };
    const idx =
      typeof pathIndexInPlayerPath === "number"
        ? pathIndexInPlayerPath
        : playerPath.indexOf(word);
    const choice =
      idx > 0 ? (optimalChoices as MoveJudgement[])[idx - 1] : undefined;
    if (choice?.isGlobalOptimal)
      return {
        label: "Global Optimal move",
        color: customColors.globalOptimalNode,
      };
    if (choice?.isLocalOptimal)
      return {
        label: "Local Optimal move",
        color: customColors.localOptimalNode,
      };
    return null;
  }, [
    word,
    startWord,
    targetWord,
    currentWord,
    gameStatus,
    pathIndexInPlayerPath,
    playerPath,
    optimalChoices,
    customColors,
  ]);

  const handleBacktrack = () => {
    if (!canBacktrackToWord) return;
    const wordIndex =
      typeof pathIndexInPlayerPath === "number"
        ? pathIndexInPlayerPath
        : playerPath.indexOf(word);
    if (wordIndex === -1) {
      console.warn("Word not found in player path for backtracking.");
      return;
    }
    backtrackToWord(word, wordIndex);
    onDismiss();
  };

  if (!word) {
    return null;
  }

  // Use locally loaded definitions or fallback to store definitions
  const definitions = localDefinitions || definitionsData?.[word] || [];

  if (!visible && opacity.value === 0) {
    return null;
  }

  return (
    <Portal>
      <Modal visible={visible} onDismiss={onDismiss} centered>
        <Animated.View
          style={[styles.animatedDialogContainer, animatedContentStyle]}
        >
          <View
            style={[
              styles.dialogBase,
              {
                backgroundColor: colors.surface,
                borderColor: colors.outline,
              },
            ]}
          >
            <ModalHeader
              title={word}
              titleColor={role?.color}
              onClose={onDismiss}
              titleStyle={styles.wordTitle}
            />
            {(role || detourInfo) && (
              <View style={styles.metaRow}>
                {role && (
                  <Text
                    style={[
                      styles.roleLabel,
                      { color: colors.onSurfaceVariant },
                    ]}
                  >
                    {role.label}
                  </Text>
                )}
                {detourInfo && (
                  <Text
                    style={[
                      styles.roleLabel,
                      { color: colors.onSurfaceVariant },
                    ]}
                  >
                    Gold path went to{" "}
                    <Text
                      style={{
                        fontWeight: "600",
                        color: detourInfo.isGlobalOptimal
                          ? customColors.globalOptimalNode
                          : customColors.localOptimalNode,
                      }}
                    >
                      {detourInfo.goldNext}
                    </Text>
                  </Text>
                )}
              </View>
            )}
            <View
              style={styles.contentContainer}
              onLayout={(e: LayoutChangeEvent) =>
                setViewportH(e.nativeEvent.layout.height)
              }
            >
              <ScrollView
                style={styles.scrollView}
                showsVerticalScrollIndicator={true}
                contentContainerStyle={styles.scrollContent}
                onContentSizeChange={(_w, h) => setContentH(h)}
                onScroll={onScroll}
                scrollEventThrottle={32}
              >
                {isLoadingDefinitions ? (
                  <View style={{ padding: 20, alignItems: "center" }}>
                    <ActivityIndicator size="small" color={colors.primary} />
                    <Text
                      style={{
                        marginTop: 8,
                        color: colors.onSurfaceVariant,
                        fontSize: 14,
                      }}
                    >
                      Loading definition...
                    </Text>
                  </View>
                ) : definitions.length > 0 ? (
                  definitions
                    .slice()
                    .reverse()
                    .map((definition, index) => (
                      <React.Fragment key={index}>
                        {index > 0 && (
                          <Divider
                            style={[
                              styles.divider,
                              { backgroundColor: colors.outlineVariant },
                            ]}
                          />
                        )}
                        <View style={styles.definitionRow}>
                          {definitions.length > 1 && (
                            <Text
                              style={[
                                styles.definitionIndex,
                                { color: colors.onSurfaceVariant },
                              ]}
                            >
                              {index + 1}
                            </Text>
                          )}
                          <Text
                            style={[
                              styles.definition,
                              { color: colors.onSurface },
                            ]}
                          >
                            {definition}
                          </Text>
                        </View>
                      </React.Fragment>
                    ))
                ) : (
                  <Text
                    style={[styles.noDefinition, { color: colors.onSurface }]}
                  >
                    No definition available for this word.
                  </Text>
                )}
              </ScrollView>
              {showFade && (
                <BottomFade
                  color={colors.surface}
                  style={styles.fade}
                  testID="definition-fade"
                />
              )}
            </View>
            {canBacktrackToWord && (
              <View style={styles.actions}>
                <AppButton
                  variant="secondary"
                  icon="step-backward"
                  label="Backtrack here"
                  onPress={handleBacktrack}
                  testID="backtrack-here"
                  style={{ borderColor: role?.color }}
                />
              </View>
            )}
          </View>
        </Animated.View>
      </Modal>
    </Portal>
  );
};

const styles = StyleSheet.create({
  animatedDialogContainer: {
    width: "100%",
    maxWidth: 500,
    maxHeight: "80%",
  },
  // Sizes to its content, up to the 80% cap; the body scrolls past that.
  dialogBase: {
    borderRadius: 16,
    borderWidth: 1,
    flexShrink: 1,
    overflow: "hidden",
  },
  wordTitle: {
    fontSize: 22,
    lineHeight: 28,
  },
  metaRow: {
    paddingHorizontal: 20,
    paddingTop: 10,
    gap: 2,
  },
  roleLabel: {
    fontSize: 13,
  },
  contentContainer: {
    flexShrink: 1,
  },
  scrollView: {
    flexGrow: 0,
  },
  scrollContent: {
    paddingHorizontal: 20,
    paddingTop: 12,
    paddingBottom: 16,
  },
  definitionRow: {
    flexDirection: "row",
    gap: 10,
  },
  definitionIndex: {
    fontSize: 13,
    fontWeight: "600",
    lineHeight: 24,
    minWidth: 14,
  },
  definition: {
    flex: 1,
    fontSize: 16,
    lineHeight: 24,
  },
  noDefinition: {
    fontSize: 16,
    fontStyle: "italic",
    textAlign: "center",
    marginVertical: 20,
  },
  divider: {
    height: 1,
    marginVertical: 10,
  },
  fade: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: 24,
  },
  actions: {
    paddingHorizontal: 20,
    paddingBottom: 16,
    paddingTop: 4,
  },
});

export default WordDefinitionDialog;
